import { useContext, useEffect } from "react";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import {
  AuthContext,
  AuthUtils,
  AUTHENTICATED,
  CREATING_SESSION,
} from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { AppHeader } from "@bfc-software/bfc-web-ui/ui/shared/app-header";
import { LocationSelector } from "@bfc-software/bfc-web-ui/ui/shared/location-selector";
import { OutboundSidebar } from "@bfc-software/bfc-web-ui/ui/outbound/outbound-sidebar";

import styles from "./_layout.module.css";

/**
 * Outbound's shell: the header, the navigation rail, and the warehouse the
 * screens below run against.
 */
export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  const { state, selectLocation } = useContext(AuthContext);
  const navigate = useNavigate();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  /**
   * Inside a product the URL owns the warehouse and the session follows it,
   * which is what makes a bookmark work. Reconciliation happens here and nowhere
   * else: the header selector only navigates, because two effects reconciling in
   * opposite directions loop.
   *
   * A warehouse the organisation does not have goes back to the front door.
   * Re-minting against it would 401 on every pass, and rendering it shows an
   * empty product that reads as an empty warehouse.
   *
   * CREATING_SESSION counts as out of sync. The organisation's warehouses are
   * known in that state, but the AAA token is still the previous warehouse's -
   * the org layout keeps this subtree mounted while the session is re-minted, so
   * without this the screen below would fetch against the warehouse being left.
   */
  const authenticated = state.status === AUTHENTICATED;
  const remintingSession = state.status === CREATING_SESSION;
  const orgKnown = authenticated || remintingSession;

  const sessionWarehouse = authenticated ? state.locationId : null;
  const orgWarehouses = orgKnown
    ? AuthUtils.getLocations(state, state.orgId)
    : [];
  const urlWarehouseIsKnown = orgWarehouses.some(
    (warehouse) => warehouse.warehouseId === params.location,
  );
  const warehouseIsUnknown = orgKnown && !urlWarehouseIsKnown;
  const needsWarehouseSync =
    urlWarehouseIsKnown && sessionWarehouse !== params.location;

  useEffect(() => {
    if (!authenticated) return;
    if (!needsWarehouseSync) return;

    selectLocation(params.location);
  }, [authenticated, needsWarehouseSync, params.location, selectLocation]);

  useEffect(() => {
    if (!warehouseIsUnknown) return;

    navigate({
      to: "/$lang/orgs/$org/outbound",
      params: { lang: params.lang, org: params.org },
      replace: true,
    });
  }, [warehouseIsUnknown, navigate, params.lang, params.org]);

  return (
    <>
      <AppHeader actions={<LocationSelector className={styles.warehouse} />} />
      <div className={styles.shell}>
        <OutboundSidebar />
        {/*
          Held back until the session matches the warehouse in the URL, so a
          screen never loads against the one being left - or against a warehouse
          the organisation does not have, which is on its way to the front door.
          The rail stays mounted through it: it is scoped by the URL rather than
          the session, so blanking it would make a warehouse switch look like a
          navigation.
        */}
        <div className={styles.content}>
          {needsWarehouseSync || warehouseIsUnknown ? (
            <p className={styles.switching}>{t("shell.loadingWarehouse")}</p>
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    </>
  );
}
